import { createSelector } from "reselect";
import {
  getRatingMeta,
  getDefaultImageUrl,
  getFiltersByPageLevel,
  getModelSummaryHeadingTemplate,
  getPageHeadings,
  getPageTitleFormat,
  getProsConsHeading,
  getCalculationParametersContent,
} from "../content/selectors";

import { getFilterLabels } from "../filters/selectors";

import {
  getCurrentPage,
  getSelectedSearchOptionKey,
  getFilterLookup,
  getFilterValues,
} from "../search/selectors";

import { pageLevels } from "./pageLevels";

import { paginationStyles } from "./paginationStyles";

import { loadingTypes } from "./loadingTypes";

import { replaceTokens } from "utils/tokens";

import { journeyMake,
  journeyType,
  journeyBudget,
  filterMake,
  filterRange,
  filterModel,
  filterPriceFrom,
  filterPriceTo,
  filterStatus } from "./constants";

const getResults = state => state.results;

const getResultsData = createSelector(
  getResults,
  results => results.data,
);
export const getMetaTag = createSelector(
  getResultsData,
  (data) => data && data.results && data.results.metaTag ? data.results.metaTag : 'ZenAuto search results',
);
export const getCanonicalUrl = createSelector(
  getResultsData,
  (data) => data && data.results && data.results.canonicalUrl ? data.results.canonicalUrl : null,
);
export const getRobots = createSelector(
  getResultsData,
  (data) => data && data.results && data.results.robotsMeta ? data.results.robotsMeta : null,
);
const isInitialised = createSelector(
  getResults,
  results => results.isInitialised,
);

export const getSeoTextBlock = createSelector(
  isInitialised,
  getResultsData,
  (isInitialised, data) => {
    if (!isInitialised) return {};
    const { seoTextBlock, displayMoreText, linesToDisplay } = data.results;
    return {
      seoTextBlock: seoTextBlock || null,
      displayMoreText: displayMoreText || "Show More",
      linesToDisplay: linesToDisplay || 3,
    };
  },
);

export const isLoadingMoreResults = createSelector(
  getResults,
  results => results.isLoadingMore,
);

export const isResultsLoading = createSelector(
  isInitialised,
  getResults,
  (isInitialised, results) => !isInitialised || results.isLoading,
);

const _getPageLevel = (query, filtersByPageLevel) => {
  if (
    pageLevels.derivative in filtersByPageLevel &&
    filtersByPageLevel[pageLevels.derivative].every(filterKey =>
      query.filters.some(f => f.key === filterKey),
    )
  )
    return pageLevels.derivative;

  if (
    pageLevels.model in filtersByPageLevel &&
    filtersByPageLevel[pageLevels.model].every(filterKey =>
      query.filters.some(f => f.key === filterKey),
    )
  )
    return pageLevels.model;

  return pageLevels.range;
};

export const getLoadingType = createSelector(
  isInitialised,
  getResults,
  state => state.search.query,
  getFiltersByPageLevel,
  (isInitialised, results, query, filtersByPageLevel) => {
    if (!isInitialised || !results.isLoading) return null;

    const previousQuery = results.query;

    const isSortEqual = (option1, option2) =>
      option1 === option2 || (option1 && option2 && option1.value === option2.value);

    const checks = {
      hasSameSortBy: isSortEqual(query.sortBy, previousQuery.sortBy),
      hasSamefilters: query.filters === previousQuery.filters,
      isOnSamePage: previousQuery.page === query.page,
    };

    let type = loadingTypes.level;

    if (!checks.hasSameSortBy && checks.hasSamefilters && query.page === 1) {
      type = loadingTypes.sorting;
    } else if (!checks.isOnSamePage && checks.hasSameSortBy && checks.hasSamefilters) {
      type = loadingTypes.pagination;
    } else if (
      _getPageLevel(query, filtersByPageLevel) === _getPageLevel(previousQuery, filtersByPageLevel)
    ) {
      type = loadingTypes.inPage;
    }

    return type;
  },
);

export const hideResultCount = createSelector(
  isResultsLoading,
  getLoadingType,
  (isLoading, type) => isLoading && ![loadingTypes.sorting, loadingTypes.pagination].includes(type),
);

export const showOverlay = createSelector(
  getLoadingType,
  type => [loadingTypes.sorting, loadingTypes.pagination, loadingTypes.inPage].includes(type),
);

export const getCars = createSelector(
  isInitialised,
  getDefaultImageUrl,
  getRatingMeta,
  getResultsData,
  (isInitialised, defaultImage, ratingMeta, data) =>
    !isInitialised
      ? []
      : data.results.items.map(item => ({
          ...item,
          image: item.image || defaultImage,
          ratingMeta,
          specifications: (item.specifications || []).slice(0, 1),
        })),
);

export const getPageLevel = createSelector(
  getResultsData,
  data => data && (data.results && data.results.level),
);

export const getCalculationParams = createSelector(
  isInitialised,
  getResultsData,
  (isInitialised, data) => (!isInitialised ? {} : data.calculationParameters),
);

export const getCalculationSummary = createSelector(
  isInitialised,
  getResultsData,
  getCalculationParametersContent,
  (isInitialised, data, content) => {
    if (!isInitialised) return "";

    const { calculationParameters } = data;
    const message = replaceTokens(content, calculationParameters);

    return message;
  },
);

export const showRatings = createSelector(
  getCars,
  cars => cars.some(car => car.rating),
);

export const getResultCount = state =>
  isInitialised(state) ? getResultsData(state).results.totalResults : 0;

export const hasNoResults = state => getResultCount(state) === 0;

export const hasNoSearchResults = state => getNoSearchResults(state);

const getNoSearchResults = state => {
  return state?.content?.data?.noSearchResults ? true : false;
};

const getCounts = (filterKey, options) =>
  options.map(option => ({
    key: `${filterKey}_${option.key}`,
    count: option.count,
  }));

const buildFilterCountLookup = state => {
  const lookup = getResultsData(state)
    .filters.map(filter => getCounts(filter.key, filter.options))
    .reduce((arr, filterCount) => arr.concat(filterCount), [])
    .reduce((obj, optionCount) => {
      obj[optionCount.key] = optionCount.count;
      return obj;
    }, {});

  return lookup;
};

export const getFilterCountLookup = state =>
  !isInitialised(state) ? {} : buildFilterCountLookup(state);

const _getFilterLabels = state => getFilterLabels(state);

export const getHeading = createSelector(
  getSelectedSearchOptionKey,
  getFilterLookup,
  getPageHeadings,
  getFilterValues,
  (searchBy, query, headings, filters) => {
    const defaultHeading = "Search results";

    if (!Object.keys(query).length || !searchBy) return defaultHeading;

    try {
      var filterKeys = filters.map(f => f.key);
      const makeFilterKeys = [filterMake, filterRange, filterModel];
      const budgetFilterKeys = [filterPriceFrom, filterPriceTo];

      //Based on the searchBy filter down to the only filters required to determine the header.
      switch (searchBy) {
        case journeyMake:
          filterKeys = filterKeys.filter(f => makeFilterKeys.includes(f));
          if (filterKeys.includes(filterModel)) {
            filterKeys = [filterMake, filterModel];
          }
          break;
        case journeyType:
          filterKeys = filterKeys.filter(f => f === searchBy);
          break;
        case journeyBudget:
          filterKeys = filterKeys.filter(f => budgetFilterKeys.includes(f));
          break;
      }

      //Get the heading that match filters selected on number of tokens and token keys.
      var match = headings.filter(h => h.tokens.every(t => filterKeys.includes(t)) && h.tokens.length > 0 && h.tokens.length === filterKeys.length)[0];

      if (match) {
        const tokenValues = match.tokens.map(token => [token, filters.find(f => f.key === token).value])
          .map(tv => [tv[0], (typeof tv[1] == 'string' && !(/^\d+$/.test(tv[1]))) ? tv[1].replaceAll('%20', ' ').replaceAll('-', ' ') : match.journey === 'budget' ? `£${tv[1]}`: tv[1]]);

        const heading = replaceTokens(match.format, tokenValues);

        return heading;
      }
      else {
        const headingsWithFilters = headings.filter(h => h.filters && h.tokens.length === 0);

        var template = headingsWithFilters.filter(h => filters.map(f => f.key).every(fk => h.filters.map(f => f.key).includes(fk)))[0];

        if (searchBy === journeyBudget && filters.map(f => f.key).includes(filterPriceFrom)) {
          template = headingsWithFilters.filter(h => filters.filter(f => budgetFilterKeys.includes(f.key)).map(f => f.key).every(fk => h.filters.filter(f => budgetFilterKeys.includes(f.key)).map(f => f.key).includes(fk)))[0];
          return template.format;
        }

        switch (filters.map(f => f.key).filter(f => f === filterStatus).length) {
          case 1:
            filters = [filters[0]];
            break;
          case 2:
            filters = filters.filter(f => f.key === filterStatus);
            break;
        }

        if (filters.map(f => f.key).includes(filterStatus)) {
          template = headingsWithFilters.filter(h => filters.map(f => f.key).every(fk => h.filters.map(f => f.key).includes(fk)) && filters.map(f => f.value).every(fv => h.filters.map(f => f.value).includes(fv)))[0];
        }

        return template.format;
      }
    } catch (e) {
      return defaultHeading;
    }
  },
);

export const getPageTitle = createSelector(
  getResultsData,
  data => data && (data.results && data.results.title),
);

export const getDefaultPageTitle = createSelector(
  getPageTitleFormat,
  getHeading,
  (titleFormat, heading) =>
    replaceTokens(titleFormat, {
      searchResultsHeading: heading,
    }),
);

const getProsCons = (heading, banner) => {
  if (!banner || !banner.prosCons) return null;

  const maxItemCount = 3;

  const getProsOrCons = (items, type) =>
    items
      .filter(item => item.type === type)
      .slice(0, maxItemCount)
      .map(item => item.text);

  return {
    heading,
    pros: getProsOrCons(banner.prosCons, "pro"),
    cons: getProsOrCons(banner.prosCons, "con"),
  };
};

export const getModelSummaryHeading = createSelector(
  getModelSummaryHeadingTemplate,
  _getFilterLabels,
  (template, filterLabels) => {
    const tokenValues = Object.entries(filterLabels).map(([key, value]) => [key, value.label]);
    return replaceTokens(template, tokenValues);
  },
);

export const getModelSummary = createSelector(
  isInitialised,
  getProsConsHeading,
  getRatingMeta,
  getResultsData,
  getDefaultImageUrl,
  getModelSummaryHeading,
  (isInitialised, heading, ratingMeta, data, defaultImage, modelSummaryHeading) =>
    isInitialised && data.results.banner !== null
      ? {
          ...data.results.banner,
          heading: modelSummaryHeading,
          imageUrl: data.results.banner.imageUrl || defaultImage,
          prosCons: getProsCons(heading, data.results.banner),
          ratingMeta,
        }
      : null,
);

export const showModelSummary = createSelector(
  getModelSummary,
  modelSummary => modelSummary !== null,
);

export const getPagination = createSelector(
  isInitialised,
  getResultsData,
  getCurrentPage,
  (isInitialised, data, currentPage) => {
    if (!isInitialised) return null;

    return {
      ...data.results.pagination,
      currentPage,
    };
  },
);

export const isPaginatingByLoadMore = createSelector(
  getPagination,
  pagination => !pagination || pagination.style === paginationStyles.buttons,
);
